import type { LucideIcon } from 'lucide-react'
import {
  AlarmClockIcon,
  BookOpenIcon,
  ClipboardListIcon,
  CompassIcon,
  FileQuestionIcon,
  KeyRoundIcon,
  LayoutTemplateIcon,
  MoonIcon,
  PencilLineIcon,
  RulerIcon,
  SearchXIcon,
} from 'lucide-react'

export type Stat = {
  value: string
  label: string
}

export type IconItem = {
  icon: LucideIcon
  text: string
}

export type PackItem = {
  icon: LucideIcon
  title: string
  text: string
}

export type PageSample = {
  src: string
  level: string
  title: string
  caption: string
}

export type Bonus = {
  title: string
  was: string
  cover: string
}

export type Testimonial = {
  quote: string
  name: string
  location: string
}

export type Faq = {
  q: string
  a: string
}

export const stats: Stat[] = [
  { value: '+70', label: "Unità Pronte all'Uso" },
  { value: '+2.000', label: 'Docenti Soddisfatti' },
  { value: '4.200+', label: 'Ore Risparmiate' },
  { value: '99%', label: 'Lo Raccomandano' },
]

export const differenceStats: Stat[] = [
  { value: '+70', label: 'Unità Pronte (A1 a C2)' },
  { value: '6', label: 'Volumi Digitali Completi' },
  { value: '100%', label: 'Download Immediato' },
]

export const beforeItems: string[] = [
  'Ore passate a pianificare ogni singola lezione da zero',
  'Esercizi di grammatica ripetitivi e noiosi',
  'Nessuna connessione chiara con il mondo reale',
  'Difficoltà nel valutare e monitorare i progressi',
  'Studenti demotivati che "odiano l\'inglese"',
]

export const afterItems: string[] = [
  '5 minuti di revisione e sei pronto per la classe',
  'Progetti linguistici visivi e coinvolgenti',
  'Applicazioni reali integrate in ogni unità',
  'Quiz, esami e rubriche già pronti',
  'Studenti che parlano con assoluta sicurezza',
]

export const recognizeItems: IconItem[] = [
  {
    icon: FileQuestionIcon,
    text: 'Arrivare in classe senza sapere esattamente come spiegare un tempo verbale complesso',
  },
  {
    icon: AlarmClockIcon,
    text: "Passare l'intera domenica a pianificare ciò che dovrebbe richiedere minuti",
  },
  {
    icon: SearchXIcon,
    text: 'Faticare a trovare schede didattiche che siano davvero di alta qualità e visive',
  },
  {
    icon: ClipboardListIcon,
    text: "Nessuna valutazione o rubrica chiara allineata con l'anno scolastico",
  },
  {
    icon: MoonIcon,
    text: 'Studenti che restano indietro perché il materiale non è adatto al loro livello',
  },
]

export const packItems: PackItem[] = [
  {
    icon: BookOpenIcon,
    title: '5 Volumi basati sui Livelli (A1–C1)',
    text: "Un curriculum completo dal principiante assoluto all'inglese accademico avanzato",
  },
  {
    icon: CompassIcon,
    title: 'Volume 6: The Master Plan',
    text: 'Il sistema operativo del docente: rubriche, calendari, placement test e schede di monitoraggio',
  },
  {
    icon: PencilLineIcon,
    title: 'Script delle Lezioni Passo dopo Passo',
    text: 'Suggerimenti che ti dicono esattamente come condurre la lezione in pochi secondi',
  },
  {
    icon: RulerIcon,
    title: 'Grammatica Visiva e Worksheets',
    text: 'Grafiche di alta qualità che rendono semplici le regole complesse',
  },
  {
    icon: LayoutTemplateIcon,
    title: 'Valutazioni ed Esami Finali',
    text: 'Strumenti di valutazione completi per ogni singolo livello (A1–C2)',
  },
  {
    icon: KeyRoundIcon,
    title: 'Answer Keys Complete',
    text: 'Correzioni istantanee, accurate e senza stress',
  },
]

export const pageSamples: PageSample[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/19w7TeT3V4MQxZfX7XkejP/Captura-de-pantalla-2026-09-01-a-las-12.50.42_p._m..webp',
    level: 'A1 · Bambini',
    title: 'Color by Number & Write the color',
    caption:
      'Attività visive pronte da stampare per il vocabolario di base, con codice colori e spazi da completare.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/dMPGdKmCCHXw6JyWFxdivo/Captura-de-pantalla-2026-09-01-a-las-12.51.46_p._m..webp',
    level: 'A2 · Unità 1',
    title: 'Come descrivere una persona in inglese',
    caption:
      'Grammatica e lessico visivi con trascrizione fonetica e un dialogo modello pronto da recitare in classe.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/5TEZmN9hj5gG6MdKjaAUnV/Captura-de-pantalla-2026-09-01-a-las-12.52.53_p._m..webp',
    level: 'B1 · Attività',
    title: 'Past simple: match, storia e scrittura',
    caption:
      'Tre attività a difficoltà crescente e il Teacher Script che ti dice esattamente cosa dire.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/wFE8ppnGDrDk3uf9wCjERj/Captura-de-pantalla-2026-09-01-a-las-12.54.04_p._m..webp',
    level: 'C1 · Flashcards',
    title: 'Vocabolario avanzato da ritagliare',
    caption:
      'Carte con verbi, avverbi, collocation e phrasal verb: esempio, definizione e immagine di contesto.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/aqGS5DymEkouBR4EM4Yo1w/Captura-de-pantalla-2026-09-01-a-las-12.55.10_p._m..webp',
    level: 'C1 · Valutazione',
    title: '«Posso farlo?» — Indicatori di padronanza',
    caption:
      'Griglia di autovalutazione su scala 1–4 per speaking, listening, reading e writing.',
  },
]

export const bonuses: Bonus[] = [
  {
    title: 'Kit Primo Giorno Perfetto™',
    was: '€19',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/3c38b9ed-7546-4e91-9045-9477c41abdc2.jpg',
  },
  {
    title: 'Programmatore Lezioni in 5 Minuti™',
    was: '€27',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/4b608dda-19e5-4e44-8955-88bc9a5b16c8.jpg',
  },
  {
    title: 'Sistema di Monitoraggio Progressi™',
    was: '€36',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/f7b701d6-d846-4261-988e-88068254e7cb.jpg',
  },
  {
    title: 'Kit Emergenza Supplenti™',
    was: '€47',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/619cd6c2-455c-4f60-88ca-690b22ba1eb9.jpg',
  },
  {
    title: "Poster Pronti per l'Aula™",
    was: '€19',
    cover:
      'https://cdn.magicpatterns.com/patterns/generated-images/8fb4b250-f479-4e6b-9cef-d86f8b1b60f9.jpg',
  },
]

export const offerRows: Array<{ label: string; value: string }> = [
  { label: 'INGLESE PRONTO™ Pack (6 Volumi Digitali)', value: '€77' },
  { label: '5 Bonus Strategici Esclusivi', value: '€148' },
]

export const testimonials: Testimonial[] = [
  {
    quote:
      'Ho recuperato le mie domeniche. La progressione da A1 a C1 è esattamente ciò di cui avevo bisogno.',
    name: 'Chiara M.',
    location: 'Milano, Italia',
  },
  {
    quote:
      'Le mappe grammaticali visive fanno una differenza enorme per i miei studenti.',
    name: 'Luca B.',
    location: 'Roma, Italia',
  },
  {
    quote:
      'Finalmente una risorsa professionale, organizzata e pronta da stampare.',
    name: 'Elena V.',
    location: 'Torino, Italia',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Come ricevo il materiale?',
    a: 'Immediatamente via email dopo il pagamento: riceverai un link per scaricare tutti i file digitali.',
  },
  {
    q: 'Funziona se sono un insegnante alle prime armi?',
    a: 'Assolutamente sì. Gli script ti dicono esattamente cosa fare in classe, passo dopo passo.',
  },
  {
    q: 'Quali livelli copre?',
    a: 'Tutto lo spettro QCER: A1, A2, B1, B2, C1 e C2, con contesti specifici per bambini, ragazzi e adulti.',
  },
  {
    q: 'Ci sono costi mensili?',
    a: 'No. È un pagamento unico per sempre, senza abbonamenti né costi nascosti.',
  },
]
